// Japanese curriculum translations
export const ja_curriculum = {
  1: {
    title: "データセット",
    subtitle: "文字レベルのトークン化",
    heading: "レベル1：データセット",
    body: `言語モデルの燃料はテキストデータです。本番環境のLLMでは、各ドキュメントはウェブページになりますが、microgptはより簡単な例として32,000個の名前を使用します。

**文字レベルのトークン化**は、各固有文字に1つの整数を割り当てます。名前データセットの場合、26個の小文字（a-z）が得られ、トークンID 0-25が割り当てられます。各トークンは単なる離散シンボルです——数字自体に意味はありません。

特殊な**BOS**（Beginning of Sequence：シーケンス開始）トークンは区切り文字として機能します。各名前はラップされます：[BOS, e, m, m, a, BOS]。モデルはBOSが新しい名前を開始し、別のBOSがそれを終了することを学習します。最終的な語彙：27トークン（26文字 + 1 BOS）。

**本番環境との違い**：tiktoken（GPT-4で使用）のような実際のトークナイザーは、約100Kトークンを持つBPE（Byte Pair Encoding）を使用し、「the」のような一般的な単語が単一のトークンになります。はるかに効率的です！`,
    question: "なぜトレーニング前に`random.shuffle(docs)`を使用するのですか？",
    answer: "モデルが順序依存のバイアスを学習するのを防ぐためです——ファイル内の出現順序に過学習するのではなく、すべての名前に対して汎化させたいのです。",
    badge: "シャッフラー"
  },
  2: {
    title: "Valueクラス",
    subtitle: "Autograd & 計算グラフ",
    heading: "レベル2：Valueクラス",
    body: `ニューラルネットワークの訓練には**勾配**が必要です：各パラメータについて、「この数値を少し増やすと、損失は上がるのか下がるのか、そしてどのくらい変化するのか？」を知る必要があります。

**Value**クラスは、autogradをゼロから実装します。各操作を、以下を知っているレゴブロックとして考えてください：
1. 出力の計算方法（順伝播）
2. 入力に対する出力の変化（局所勾配）

**例**：乗算\`a * b\`の場合、順伝播は\`a·b\`を与え、局所勾配は\`∂(a·b)/∂a = b\`と\`∂(a·b)/∂b = a\`です。

\`backward()\`メソッドは**連鎖律**を適用します：損失がLで、ノードvが子cを持ち、局所勾配が∂v/∂cの場合：

\`\`\`
∂L/∂c += (∂v/∂c) × (∂L/∂v)
\`\`\`

これは単にパスに沿って変化率を掛け合わせているだけです！例えば：「車は自転車の2倍速く、自転車は歩行の4倍速いので、車は歩行の2×4=8倍速い。」

**本番環境との違い**：PyTorchも同じことをしますが、スカラーではなくテンソル（配列）で行い、大規模な並列処理のためにGPU上で実行します。`,
    question: "なぜ`__mul__`は`(other.data, self.data)`をlocal_gradsとして保存するのですか？",
    answer: "d(a×b)/da = bかつd(a×b)/db = aだからです。他のオペランドの値を局所勾配として保存することで、backward()が自動的に連鎖律を適用できます。",
    badge: "連鎖律マスター"
  },
  3: {
    title: "埋め込み",
    subtitle: "wte + wpe行列",
    heading: "レベル3：埋め込み",
    body: `何よりもまず、各トークンは数値のベクトルにならなければなりません。microgptは**2つの学習可能なルックアップテーブル**を使用します：

**wte**（weight token embedding）：各トークンIDを16次元ベクトルにマッピングします。「これは何の文字か？」と考えてください。

**wpe**（weight position embedding）：各位置（0–15）を16次元ベクトルにマッピングします。「シーケンス内のどこにいるか？」と考えてください。

これら2つのベクトルは**要素ごとに加算**され、モデルに単一の16次元ベクトルでアイデンティティと位置情報の両方を提供します。`,
    question: "なぜトークンと位置の埋め込みを連結するのではなく加算するのですか？",
    answer: "加算すると次元が一定（16）に保たれ、倍増（32）しないため、パラメータ効率が良いです。モデルは同じ空間内でアイデンティティと位置を分離することを学習します。",
    badge: "スペースセーバー"
  },
  4: {
    title: "RMSNorm",
    subtitle: "シグナルの安定性を保つ",
    heading: "レベル4：RMSNorm",
    body: `正規化がなければ、ネットワークの深部で活性化が指数関数的に増大し、**勾配の爆発または消失**を引き起こす可能性があります。

**RMSNorm**（Root Mean Square Normalization）は、ベクトルをその二乗平均平方根が~1になるように再スケーリングします。LayerNormとは異なり、中心化（平均減算なし）をスキップするため、より安価です。

**1e-5 epsilon**は、ベクトルがすべてゼロの場合のゼロ除算を防ぎます。

microgptは、各attentionブロックとMLPの前にRMSNormを適用し、トレーニング全体を通してシグナルを良好に調整します。`,
    question: "なぜGPT-2の元のLayerNormではなくRMSNormを使用するのですか？",
    answer: "RMSNormはより単純で（平均減算なし）、同等にトレーニングできます。LLaMAやMistralなどの最新モデルで同じ理由で使用されています——品質を犠牲にすることなく効率的です。",
    badge: "安定性プロ"
  },
  5: {
    title: "マルチヘッドアテンション",
    subtitle: "Q·K/√d → softmax → V",
    heading: "レベル5：マルチヘッドアテンション",
    body: `Attentionは**通信メカニズム**です——位置tのトークンが過去のトークン（位置0..t-1）を「見る」ことができる唯一の場所です。

すべてのトークンは3つの役割に、3つの学習行列を通じて投影されます：

- **Query（Q）**：「何を探しているのか？」
- **Key（K）**：「何を含んでいるのか？」
- **Value（V）**：「選択された場合、どんな情報を提供するか？」

**スケール付きドット積**：類似度Q·K / √head_dimは各過去トークンのスコアを与えます。√head_dimで割ることで高次元でのsoftmax飽和を防ぎます。

**マルチヘッド**（ここでは4つのヘッド）は、モデルが異なるパターンに同時にattentionできるようにします——各ヘッドは異なるタイプの関係を学習します。head_dim=4の場合、4つのヘッドを結合すると依然として16次元の出力が得られます。

**因果マスキング**：未来のトークンはマスクされ（softmax前に-∞に設定）、モデルは過去のみにattentionします——自己回帰的な生成に不可欠です。

**残差接続** x = x_attn + x_residualにより、勾配がattentionブロックを完全にバイパスでき、より深いネットワークでの勾配消失を防ぎます。`,
    question: "softmax実装でmax_val減算を使用する理由は？",
    answer: "最大値を減算することで、大きなlogitsを指数化する際のオーバーフローを防ぎます（例：exp(1000) = 無限大）。数学的には等価ですが、数値的に安定します：softmax(x) = softmax(x - max)。",
    badge: "Softmax賢者"
  },
  6: {
    title: "MLP",
    subtitle: "FC1 → ReLU → FC2",
    heading: "レベル6：MLP",
    body: `アテンション（**トークン間**で情報を混合）の後、MLPは各トークンを**独立に**処理します——モデルが事実的知識を保存する場所です。

**FC1**は16次元から64次元に拡張します（4倍）。この広い空間により、モデルは複雑な組み合わせを表現できます。
**ReLU**は非線形性を追加します——これがないと、ネットワーク全体は単なる線形変換です。
**FC2**は64次元から16次元に戻します。

拡張してから縮小するパターンは「特徴検出器」として機能します——64次元層のニューロンは特定のパターンに特化します。`,
    question: "GPT-2がGeLUを使用するのに対し、microgptがReLUを使用する理由は？",
    answer: "GeLUは滑らかです（ハードなゼロカットオフがない）ため、非常に深いネットワークの勾配フローに役立ちます。小さなmicrogpt（1層）の場合、シンプルなReLUも同様に機能し、ゼロから実装するのが簡単です。",
    badge: "活性化関数エキスパート"
  },
  7: {
    title: "Transformerブロック",
    subtitle: "Pre-norm + 残差ストリーム",
    heading: "レベル7：Transformerブロック",
    body: `各コンポーネントを理解したので、それらがどのようにして完全なgpt()関数にまとまるかを見てみましょう。

アーキテクチャにはクリーンな**2レベル構造**があります：
1. 最初の1回だけの埋め込みステップ（wte + wpe）
2. transformerレイヤーの繰り返しループ（n_layer回）

各**transformerレイヤー**は同じパターンに従う2つのサブブロックです：
x_residual = x → RMSNorm → [サブブロック] → x += x_residual

この**pre-norm + 残差**パターンは現代のtransformerの主力です。残差はネットワーク内に「ハイウェイ」を作ります——勾配は逆伝播中に任意のサブブロックをスキップできます。

**残差ストリーム**の概念：xを情報を蓄積する走り続ける合計と考えてください。各レイヤーはそこから読み取り（正規化された入力を通じて）、そこに書き込みます（残差加算を通じて）。埋め込みは最初の書き込みで、LMヘッドは最後の読み取りです。

**GPT-3へのスケールアップ**（1750億パラメータ）は単に：より大きなn_embd（12288）、より多くのヘッド（96）、より深いn_layer（96）——アーキテクチャは同一です。`,
    question: "なぜRMSNormは各サブブロックの前（pre-norm）に適用されるのか、後（post-norm、元のGPT-2のように）ではなく？",
    answer: "Pre-normは残差ストリームを予測可能なスケールに保ちます——サブブロックは常に正規化された入力を受け取ります。Post-normは残差加算の後に出力を正規化しますが、これにより残差が支配的になり、初期訓練が不安定になる可能性があります。Pre-normはLLaMA、Mistral、そして現代のほとんどのtransformerに採用されています。",
    badge: "アーキテクト"
  },
  8: {
    title: "損失関数",
    subtitle: ".log()による交差エントロピー",
    heading: "レベル8：損失関数",
    body: `モデルは**最尤推定**によって訓練されます：正しい次の文字の確率を最大化します。

これは**交差エントロピーを最小化**することと等価です：\`loss = -log(p_correct)\`

モデルが自信を持って正しい場合、p_correct ≈ 1 → loss ≈ 0。
モデルが間違っているか不確実な場合、p_correct ≈ 0 → loss → ∞。

**\`.log()\`**メソッドはValueノードに逆伝播ルールを記録します：d(log x)/dx = 1/x。\`.backward()\`が呼ばれると、勾配はsoftmaxを通り、アテンションヘッドを通り、埋め込み行列まで流れます。`,
    question: "softmax内でlogitsを確率に変換するために指数関数(exp)を使用する理由は？",
    answer: "expはすべての出力が正であることを保証し（確率は≥ 0でなければならない）、滑らかな微分可能関数を作成し、logits間の差を増幅します——訓練が進むにつれてモデルをより決定的にします。",
    badge: "対数尤度"
  },
  9: {
    title: "Adamオプティマイザ",
    subtitle: "モメンタムと速度バッファ",
    heading: "レベル9：Adamオプティマイザ",
    body: `単純な勾配降下法(SGD)は遅く不安定になる可能性があります。**Adam**は各パラメータに2つのモメンタムバッファを追加します：

**m**（1次モーメント）：勾配の指数移動平均——更新方向を滑らかにします。
**v**（2次モーメント）：二乗勾配の指数移動平均——勾配がどれだけ大きいかを追跡します。

更新\`m / sqrt(v)\`は**パラメータごとの適応学習率**です：歴史的に大きな勾配を持つパラメータは小さな更新を受け（すでに学習済み）、まれなパラメータは大きな更新を受けます。

**バイアス補正**（1-β^tで除算）は、mとvがゼロに偏っている初期ステップで重要です。`,
    question: "microgptが標準の0.9ではなくbeta1=0.85を使用する理由は？",
    answer: "低いbeta1はオプティマイザを「粘りにくく」します——古い勾配をより速く忘れます。短いシーケンスで訓練される小さなモデルの場合、これにより迅速な適応が可能になり、この小さなデータセットでモメンタムがオーバーシュートするのを防ぎます。",
    badge: "Adamウィスパラー"
  },
  10: {
    title: "推論",
    subtitle: "温度サンプリング",
    heading: "レベル10：推論",
    body: `訓練後、**新しい名前をサンプリング**できます——一度に1文字ずつ自己回帰的に。

**温度**は創造性を制御します：
- **低T (0.1)**：非常に確信的、可能性の高い文字を選択 → 繰り返しの名前
- **T = 1.0**：学習した確率から直接サンプリング
- **高T (2.0)**：分布を平坦化 → よりランダムで創造的な名前

各ステップで、モデルは現在のトークンを取り、完全なトランスフォーマーを実行し、すべての文字の分布を取得し、1つをサンプリングし、次の入力としてフィードバックします。これはモデルがBOS（「完了」を意味する）を出力するまで続きます。`,
    question: "ランダムな文字ではなくBOSトークンで推論を開始する理由は？",
    answer: "BOSは学習された「名前の開始」信号です。モデルはすべての訓練例の最初にBOSを見ているため、BOSを名前の開始文字の分布（多くの場合母音と一般的な頭文字）に関連付けることを学習しています。",
    badge: "推論マスター"
  }
};
